import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useMenuIcon, useMenuName } from '@/hooks/useMenuIcon';
import { PageContainer, PageHeader } from '@/components/layout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import { Plus, Power, AlertCircle, CheckCircle2 } from 'lucide-react';
import { API_BASE_URL as BASE } from '@/api/apiConfig';
import { LoadingBlock, Spinner } from '@/components/ui/spinner';

const API = `${BASE}/api`;

// ── 타입 ────────────────────────────────────────────────────
interface AltContextItem {
  id: number;
  category: string;
  itemKey: string;
  itemLabel: string;
  description: string | null;
  promptHint: string | null;
  tooltipText: string | null;
  multiYn: 'Y' | 'N';
  requiredYn: 'Y' | 'N';
  sortOrder: number;
  isActive: boolean;
}

// 카테고리 표시명
const CATEGORY_LABELS: Record<string, string> = {
  INDUSTRY_TYPE: '업종 유형',
  SITE_TYPE: '사이트 유형',
  SERVICE_TARGET: '서비스 대상',
  IMPACT_INFO_TYPE: '강조 정보 유형',
  IMAGE_TYPE: '이미지 타입',
  OUTPUT_TONE: 'ALT 출력 톤/문체',
  MAX_LENGTH: '길이 제한',
  ENDING_STYLE: '종결 어미',
  ALT_PURPOSE: 'ALT 생성 목적',
  FORBIDDEN_INFO: '제외 정보',
};

const CATEGORY_ORDER = Object.keys(CATEGORY_LABELS);

const getAuthHeaders = (): Record<string, string> => {
  const token = localStorage.getItem('authToken') || sessionStorage.getItem('authToken');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const emptyForm = {
  itemKey: '',
  itemLabel: '',
  tooltipText: '',
  promptHint: '',
  description: '',
  sortOrder: 0,
};

export default function AltContextItemsPage() {
  const menuIcon = useMenuIcon();
  const menuName = useMenuName();

  const [grouped, setGrouped] = useState<Record<string, AltContextItem[]>>({});
  const [activeCategory, setActiveCategory] = useState<string>(CATEGORY_ORDER[0]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(emptyForm);

  const loadData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${API}/admin/alt-context-items/grouped`, {
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('코드 목록 조회에 실패했습니다.');
      const data: Record<string, AltContextItem[]> = await res.json();
      setGrouped(data);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const handleAdd = async () => {
    if (!form.itemKey.trim() || !form.itemLabel.trim()) {
      setError('코드 키와 라벨은 필수입니다.');
      return;
    }
    setSaving(true);
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API}/admin/alt-context-items`, {
        method: 'POST',
        headers: { ...getAuthHeaders(), 'Content-Type': 'application/json' },
        body: JSON.stringify({
          category: activeCategory,
          itemKey: form.itemKey.trim().toLowerCase(),
          itemLabel: form.itemLabel.trim(),
          tooltipText: form.tooltipText.trim() || null,
          promptHint: form.promptHint.trim() || null,
          description: form.description.trim() || null,
          sortOrder: Number(form.sortOrder),
        }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err?.message || '등록에 실패했습니다.');
      }
      setSuccess('코드가 등록되었습니다.');
      setForm(emptyForm);
      setShowForm(false);
      loadData();
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDeactivate = async (id: number) => {
    if (!window.confirm('이 코드를 미사용 처리하시겠습니까?')) return;
    setError(null);
    setSuccess(null);
    try {
      const res = await fetch(`${API}/admin/alt-context-items/${id}/deactivate`, {
        method: 'PATCH',
        headers: getAuthHeaders(),
      });
      if (!res.ok) throw new Error('미사용 처리에 실패했습니다.');
      setSuccess('미사용 처리되었습니다.');
      loadData();
    } catch (e: any) {
      setError(e.message);
    }
  };

  if (loading) {
    return (
      <LoadingBlock size="lg" py={20} iconClassName="text-gray-400" label="불러오는 중..." />
    );
  }

  // 백엔드에 데이터가 없는 카테고리도 탭으로 표시하여 신규 등록 가능하게
  const allCategories = CATEGORY_ORDER;

  return (
    <PageContainer maxWidth="full">
      <PageHeader 
        title={menuName || '공통코드 관리'}
        description="컨텍스트 프로필의 선택 항목(코드 그룹)을 관리합니다."
        icon={menuIcon}
      />

      {error && (
        <Alert variant="destructive">
          <AlertCircle className="w-4 h-4" />
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      )}
      {success && (
        <Alert className="bg-green-50 border-green-300">
          <CheckCircle2 className="w-4 h-4 text-green-600" />
          <AlertDescription className="text-green-800">{success}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardContent className="p-0">
          <Tabs value={activeCategory} onValueChange={v => { setActiveCategory(v); setShowForm(false); setForm(emptyForm); }}>
            <div className="border-b px-4 pt-4 overflow-x-auto">
              <TabsList className="flex-wrap h-auto gap-1">
                {allCategories.map(cat => (
                  <TabsTrigger key={cat} value={cat} className="text-xs">
                    {CATEGORY_LABELS[cat] || cat}
                    {grouped[cat] && (
                      <Badge className="ml-1 text-xs px-1 py-0 bg-gray-200 text-gray-700">
                        {grouped[cat].filter(i => i.isActive).length}
                      </Badge>
                    )}
                  </TabsTrigger>
                ))}
              </TabsList>
            </div>

            {allCategories.map(cat => {
              const items = grouped[cat] ?? [];
              return (
                <TabsContent key={cat} value={cat} className="m-0">
                  <div className="p-4 space-y-4">
                    {/* 카테고리 정보 */}
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">{CATEGORY_LABELS[cat] || cat}</h3>
                        <Badge className="text-xs border border-gray-400 text-gray-600 bg-transparent">
                          {items[0]?.multiYn === 'Y' ? '복수선택' : '단일선택'}
                        </Badge>
                        {items.some(i => i.requiredYn === 'Y') && (
                          <Badge className="text-xs bg-red-100 text-red-700">필수</Badge>
                        )}
                      </div>
                      <Button size="sm" onClick={() => setShowForm(v => !v)}>
                        <Plus className="w-4 h-4 mr-1" />
                        코드 등록
                      </Button>
                    </div>

                    {/* 등록 폼 */}
                    {showForm && (
                      <Card className="bg-gray-50 border-dashed">
                        <CardHeader className="pb-3">
                          <CardTitle className="text-base">신규 코드 등록</CardTitle>
                        </CardHeader>
                        <CardContent className="space-y-3">
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                            <div className="space-y-1">
                              <Label>코드 키 (영문, 필수)</Label>
                              <Input
                                placeholder="예: new_industry"
                                value={form.itemKey}
                                onChange={e => setForm(prev => ({ ...prev, itemKey: e.target.value }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>라벨 (한글, 필수)</Label>
                              <Input
                                placeholder="예: 신규 업종"
                                value={form.itemLabel}
                                onChange={e => setForm(prev => ({ ...prev, itemLabel: e.target.value }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>툴팁 텍스트</Label>
                              <Input
                                placeholder="사용자에게 보여줄 설명"
                                value={form.tooltipText}
                                onChange={e => setForm(prev => ({ ...prev, tooltipText: e.target.value }))}
                              />
                            </div>
                            <div className="space-y-1">
                              <Label>정렬 순서</Label>
                              <Input
                                type="number"
                                value={form.sortOrder}
                                onChange={e => setForm(prev => ({ ...prev, sortOrder: Number(e.target.value) }))}
                              />
                            </div>
                          </div>
                          <div className="space-y-1">
                            <Label>프롬프트 힌트 (AI에 전달될 문장)</Label>
                            <textarea
                              className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                              placeholder="이 이미지는 ... 입니다. (AI 프롬프트에 자동 삽입)"
                              value={form.promptHint}
                              onChange={e => setForm(prev => ({ ...prev, promptHint: e.target.value }))}
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button variant="outline" size="sm" onClick={() => { setShowForm(false); setForm(emptyForm); }}>
                              취소
                            </Button>
                            <Button size="sm" onClick={handleAdd} disabled={saving}>
                              {saving ? <Spinner size="sm" className="mr-1" /> : null}
                              등록
                            </Button>
                          </div>
                        </CardContent>
                      </Card>
                    )}

                    {/* 코드 목록 테이블 */}
                    {items.length === 0 ? (
                      <div className="text-center text-gray-400 py-10 text-sm">
                        등록된 코드가 없습니다.
                      </div>
                    ) : (
                      <div className="overflow-x-auto">
                        <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>코드 키</TableHead>
                            <TableHead>라벨</TableHead>
                            <TableHead>툴팁</TableHead>
                            <TableHead className="text-center">정렬</TableHead>
                            <TableHead className="text-center">상태</TableHead>
                            <TableHead className="text-center">작업</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {items.map(item => (
                            <TableRow key={item.id} className={!item.isActive ? 'opacity-40' : ''}>
                              <TableCell>
                                <code className="text-xs bg-gray-100 px-1 py-0.5 rounded">{item.itemKey}</code>
                              </TableCell>
                              <TableCell className="font-medium">{item.itemLabel}</TableCell>
                              <TableCell className="text-xs text-gray-500 max-w-[200px] truncate" title={item.tooltipText ?? ''}>
                                {item.tooltipText || '-'}
                              </TableCell>
                              <TableCell className="text-center text-sm">{item.sortOrder}</TableCell>
                              <TableCell className="text-center">
                                {item.isActive ? (
                                  <Badge className="bg-green-100 text-green-800 text-xs">사용</Badge>
                                ) : (
                                  <Badge className="bg-gray-100 text-gray-600 text-xs">미사용</Badge>
                                )}
                              </TableCell>
                              <TableCell className="text-center">
                                {item.isActive && (
                                  <Button
                                    size="sm"
                                    variant="outline"
                                    className="text-xs h-7 text-red-600 border-red-200 hover:bg-red-50"
                                    onClick={() => handleDeactivate(item.id)}
                                  >
                                    <Power className="w-3 h-3 mr-1" />
                                    미사용
                                  </Button>
                                )}
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                      </div>
                    )}
                  </div>
                </TabsContent>
              );
            })}
          </Tabs>
        </CardContent>
      </Card>
    </PageContainer>
  );
}
